/**
 * DeletedRecords — operator-grade view of soft-deleted transactions
 * and invoices, with one-click restore (single + by deleted_request_id).
 *
 * Backed by:
 *   GET  /api/transactions/deleted?deleted_request_id=&limit=
 *   GET  /api/invoices-deleted?deleted_request_id=&limit=
 *   POST /api/transactions/{id}/restore
 *   POST /api/transactions/restore-by-request   {deleted_request_id}
 *   POST /api/invoices/{id}/restore
 *   POST /api/invoices/restore-by-request       {deleted_request_id}
 *
 * Bulk delete recovery: every soft-delete API returns a
 * `deleted_request_id`. Operators paste that id into the box on this
 * page and one click un-does the entire batch.
 */
import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, Badge } from "../../components/ui-extras";
import {
  ArrowsClockwise,
  ArrowUUpLeft,
  Trash,
  Receipt,
  ArrowsLeftRight,
} from "@phosphor-icons/react";

const TABS = [
  { key: "transactions", label: "Transactions", icon: ArrowsLeftRight },
  { key: "invoices", label: "Invoices", icon: Receipt },
];

function fmtDate(s) {
  if (!s) return "—";
  return String(s).slice(0, 19).replace("T", " ");
}

function fmtMoney(n) {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  return isNaN(v) ? "—" : `$${v.toFixed(2)}`;
}

export default function DeletedRecords() {
  const [tab, setTab] = useState("transactions");
  const [rows, setRows] = useState([]);
  const [requestIdFilter, setRequestIdFilter] = useState("");
  const [restoreReqId, setRestoreReqId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (requestIdFilter.trim()) params.deleted_request_id = requestIdFilter.trim();
      const path = tab === "transactions" ? "/transactions/deleted" : "/invoices-deleted";
      const res = await api.get(path, { params });
      setRows(tab === "transactions" ? res.data.transactions : res.data.invoices);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load deleted records");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, requestIdFilter]);

  const restoreOne = async (id) => {
    if (busy) return;
    setBusy(true);
    try {
      const path =
        tab === "transactions"
          ? `/transactions/${id}/restore`
          : `/invoices/${id}/restore`;
      const res = await api.post(path);
      toast.success(
        tab === "transactions"
          ? `Restored 1 transaction${res.data.invoice_restored ? " + invoice" : ""}`
          : `Restored invoice (re-attached ${res.data.reattached_lines || 0} lines)`,
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setBusy(false);
    }
  };

  const restoreByRequest = async () => {
    const drid = restoreReqId.trim();
    if (!drid) {
      toast.error("Paste a deleted_request_id first.");
      return;
    }
    if (!window.confirm(`Restore EVERY row soft-deleted by request ${drid}? This will revert the bulk delete.`)) return;
    setBusy(true);
    try {
      const path =
        tab === "transactions"
          ? "/transactions/restore-by-request"
          : "/invoices/restore-by-request";
      const res = await api.post(path, { deleted_request_id: drid });
      const summary =
        tab === "transactions"
          ? `Restored ${res.data.restored_count} transactions, ${res.data.invoices_restored} invoices`
          : `Restored ${res.data.restored_count} invoices, re-attached ${res.data.reattached_lines} lines`;
      toast.success(summary);
      setRestoreReqId("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk restore failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="deleted-records-page">
      <PageHeader
        title="Deleted Records"
        subtitle="Soft-deleted transactions and invoices. Restore by row or by deleted_request_id."
        actions={
          <button className="gf-btn gf-btn-outline" onClick={load} data-testid="refresh-deleted">
            <ArrowsClockwise size={14} /> Refresh
          </button>
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-200" data-testid="deleted-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`deleted-tab-${t.key}`}
              className={`px-4 py-2 text-sm font-bold flex items-center gap-2 border-b-2 -mb-px ${
                active
                  ? "border-zinc-900 text-zinc-900"
                  : "border-transparent text-zinc-500 hover:text-zinc-800"
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Bulk-restore-by-request bar */}
      <div className="gf-card gf-card-body" data-testid="bulk-restore-bar">
        <div className="overline mb-2">RESTORE BY DELETED_REQUEST_ID (bulk recovery)</div>
        <div className="flex flex-wrap gap-2 items-end">
          <input
            className="gf-input min-w-[260px] font-mono"
            placeholder="del_XXXXXXXXXXXXXXXX"
            value={restoreReqId}
            onChange={(e) => setRestoreReqId(e.target.value)}
            data-testid="bulk-restore-input"
          />
          <button
            className="gf-btn gf-btn-primary"
            onClick={restoreByRequest}
            disabled={busy || !restoreReqId.trim()}
            data-testid="bulk-restore-button"
          >
            <ArrowUUpLeft size={14} /> Restore batch
          </button>
          <div className="text-xs text-zinc-500 ml-2">
            The id was shown in the toast / API response when you ran the delete.
          </div>
        </div>
      </div>

      {/* Filter row */}
      <div className="gf-card gf-card-body flex flex-wrap gap-3 items-end">
        <div>
          <div className="overline mb-1">FILTER BY REQUEST ID</div>
          <input
            className="gf-input font-mono"
            placeholder="optional · del_..."
            value={requestIdFilter}
            onChange={(e) => setRequestIdFilter(e.target.value)}
            data-testid="filter-request-id"
          />
        </div>
        <div className="text-xs text-zinc-500">Showing {rows.length} row(s).</div>
      </div>

      {/* Table */}
      <div className="gf-card">
        <div className="overflow-auto">
          {tab === "transactions" ? (
            <table className="gf-table" data-testid="deleted-transactions-table">
              <thead>
                <tr>
                  <th>DELETED AT</th>
                  <th>BY</th>
                  <th>REASON</th>
                  <th>REQUEST ID</th>
                  <th>TX ID</th>
                  <th>CUSTOMER</th>
                  <th>NETWORK</th>
                  <th>DATE</th>
                  <th className="text-right">AMOUNT</th>
                  <th>STATUS</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={11} className="text-center text-zinc-400 py-8">Loading…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={11} className="text-center text-zinc-400 py-8">
                    No soft-deleted transactions match the current filters.
                  </td></tr>
                )}
                {rows.map((r, i) => (
                  <tr key={r.transaction_id} data-testid={`deleted-tx-row-${i}`}>
                    <td className="font-mono text-xs">{fmtDate(r.deleted_at)}</td>
                    <td className="text-xs text-zinc-600">{r.deleted_by_email || "—"}</td>
                    <td className="text-xs text-zinc-700 max-w-[180px] truncate" title={r.deleted_reason || ""}>
                      {r.deleted_reason || "—"}
                    </td>
                    <td className="font-mono text-[10px] text-zinc-400">{r.deleted_request_id || "—"}</td>
                    <td className="font-mono text-[10px]">{r.transaction_id}</td>
                    <td className="text-xs">{r.customer_name || "—"}</td>
                    <td className="text-xs uppercase">{r.network}</td>
                    <td className="font-mono text-xs">{r.tran_date}</td>
                    <td className="gf-num">{fmtMoney(r.customer_amount)}</td>
                    <td><Badge variant="warning">{r.status}</Badge></td>
                    <td className="text-right">
                      <button
                        className="gf-btn gf-btn-outline"
                        disabled={busy}
                        onClick={() => restoreOne(r.transaction_id)}
                        data-testid={`restore-tx-${r.transaction_id}`}
                      >
                        <ArrowUUpLeft size={12} /> Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="gf-table" data-testid="deleted-invoices-table">
              <thead>
                <tr>
                  <th>DELETED AT</th>
                  <th>BY</th>
                  <th>REASON</th>
                  <th>REQUEST ID</th>
                  <th>INVOICE #</th>
                  <th>CUSTOMER</th>
                  <th>ISSUE DATE</th>
                  <th className="text-right">TOTAL</th>
                  <th>STATUS</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={10} className="text-center text-zinc-400 py-8">Loading…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={10} className="text-center text-zinc-400 py-8">
                    No soft-deleted invoices match the current filters.
                  </td></tr>
                )}
                {rows.map((r, i) => (
                  <tr key={r.invoice_id} data-testid={`deleted-inv-row-${i}`}>
                    <td className="font-mono text-xs">{fmtDate(r.deleted_at)}</td>
                    <td className="text-xs text-zinc-600">{r.deleted_by_email || "system"}</td>
                    <td className="text-xs text-zinc-700 max-w-[180px] truncate" title={r.deleted_reason || ""}>
                      {r.deleted_reason || "—"}
                    </td>
                    <td className="font-mono text-[10px] text-zinc-400">{r.deleted_request_id || "—"}</td>
                    <td className="font-mono text-xs">{r.invoice_number}</td>
                    <td className="text-xs">{r.customer_name || "—"}</td>
                    <td className="font-mono text-xs">{r.issue_date}</td>
                    <td className="gf-num">{fmtMoney(r.total)}</td>
                    <td><Badge variant="warning">{r.status}</Badge></td>
                    <td className="text-right">
                      <button
                        className="gf-btn gf-btn-outline"
                        disabled={busy}
                        onClick={() => restoreOne(r.invoice_id)}
                        data-testid={`restore-inv-${r.invoice_id}`}
                      >
                        <ArrowUUpLeft size={12} /> Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="text-xs text-zinc-500 flex items-center gap-2">
        <Trash size={12} /> Soft-deleted rows stay in the database until a Mongo TTL or explicit hard-delete clears them.
        Hard delete requires <code>ALLOW_DESTRUCTIVE_ENDPOINTS=true</code> + typed confirmation.
      </div>
    </div>
  );
}
